import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage, LANGUAGES } from '../../i18n/LanguageContext';
import { useMarket, MARKETS } from '../../contexts/MarketContext';
import { useAuth } from '../../contexts/AuthContext';
import AuthModal from '../AuthModal';
import { Menu, X, ChevronDown, Globe, User, LogOut, MapPin } from 'lucide-react';

// PLEXTERRA nav labels (5 languages, English fallback)
const NAV_LABELS = {
  fr: { home: 'Accueil', about: 'À propos', services: 'Services', technologies: 'Technologies', sectors: 'Secteurs desservis', projects: 'Projets', successStories: 'Réussites', blog: 'Blog', careers: 'Carrières', contact: 'Contact' },
  en: { home: 'Home', about: 'About', services: 'Services', technologies: 'Technologies', sectors: 'Sectors', projects: 'Projects', successStories: 'Stories', blog: 'Blog', careers: 'Careers', contact: 'Contact' },
  es: { home: 'Inicio', about: 'Acerca de', services: 'Servicios', technologies: 'Tecnologías', sectors: 'Sectores', projects: 'Proyectos', successStories: 'Casos', blog: 'Blog', careers: 'Carreras', contact: 'Contacto' },
  ar: { home: 'الرئيسية', about: 'من نحن', services: 'الخدمات', technologies: 'التقنيات', sectors: 'القطاعات', projects: 'المشاريع', successStories: 'قصص', blog: 'المدونة', careers: 'الوظائف', contact: 'اتصل بنا' },
  zh: { home: '首页', about: '关于', services: '服务', technologies: '技术', sectors: '行业', projects: '项目', successStories: '案例', blog: '博客', careers: '招聘', contact: '联系' }
};

const AUTH_LABELS = {
  fr: { login: 'Connexion', logout: 'Déconnexion', account: 'Mon compte' },
  en: { login: 'Sign In', logout: 'Sign Out', account: 'My Account' },
  es: { login: 'Iniciar Sesión', logout: 'Cerrar Sesión', account: 'Mi Cuenta' },
  ar: { login: 'تسجيل الدخول', logout: 'تسجيل الخروج', account: 'حسابي' },
  zh: { login: '登录', logout: '退出', account: '我的账户' }
};

const Header = () => {
  const { language, setLanguage } = useLanguage();
  const { market, setMarket, currencyLabel } = useMarket();
  const { user, isAuthenticated, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [marketDropdownOpen, setMarketDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;
  const currentLang = LANGUAGES.find(l => l.code === language);
  const nl = NAV_LABELS[language] || NAV_LABELS.en;
  const ul = AUTH_LABELS[language] || AUTH_LABELS.en;

  const team = { fr: 'Équipe', en: 'Team', es: 'Equipo', ar: 'الفريق', zh: '团队' };
  const teamLabel = team[language] || team.en;

  const navLinks = [
    { path: '/', label: nl.home },
    { path: '/about', label: nl.about },
    { path: '/team', label: teamLabel },
    { path: '/services', label: nl.services },
    { path: '/technologies', label: nl.technologies },
    { path: '/secteurs', label: nl.sectors },
    { path: '/projets', label: nl.projects },
    { path: '/success-stories', label: nl.successStories },
    { path: '/blog', label: nl.blog },
    { path: '/careers', label: nl.careers },
    { path: '/contact', label: nl.contact }
  ];

  return (
    <header className="bg-white border-b border-gray-100 sticky top-0 z-50" data-testid="main-header">
      <nav className="max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6">
        <div className="flex justify-between items-center gap-4 2xl:gap-6 h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="logo-link">
            <img
              src="/plexterra-logo.jpeg"
              alt="PLEXTERRA"
              className="w-11 h-11 lg:w-14 lg:h-14 rounded-full object-cover"
              data-testid="logo-image"
            />
            <div className="flex flex-col leading-none">
              <span className="font-serif text-lg lg:text-xl tracking-[0.2em] text-[#0B1E3F]">PLEXTERRA</span>
              <span className="text-[10px] tracking-[0.25em] text-gray-500 mt-1 uppercase hidden 2xl:block">Développement immobilier</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden 2xl:flex items-center gap-0.5 xl:gap-1 flex-1 justify-center">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-2 xl:px-3 py-2 text-[13px] xl:text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive(link.path)
                    ? 'text-[#C6A15A]'
                    : 'text-[#0B1E3F] hover:text-[#C6A15A]'
                }`}
                data-testid={`nav-link${link.path === '/' ? '-home' : link.path.replace(/\//g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side: Market + Language + Auth + Mobile */}
          <div className="flex items-center gap-1 xl:gap-2 flex-shrink-0">
            {/* Market — hidden below xl to save horizontal space */}
            <div
              className="relative hidden 2xl:block"
              onMouseEnter={() => setMarketDropdownOpen(true)}
              onMouseLeave={() => setMarketDropdownOpen(false)}
            >
              <button className="flex items-center gap-1 px-2 py-2 text-xs font-medium text-[#0B1E3F] hover:text-[#C6A15A] transition-colors" data-testid="market-toggle">
                <MapPin className="h-4 w-4" />
                <span>{market}</span>
                <span className="text-gray-400">({currencyLabel})</span>
                <ChevronDown className="h-3 w-3" />
              </button>
              {marketDropdownOpen && (
                <div className="absolute top-full right-0 w-44 bg-white shadow-lg rounded-sm py-2 border border-gray-100 z-50">
                  {MARKETS.map((m) => (
                    <button
                      key={m.code}
                      onClick={() => { setMarket(m.code); setMarketDropdownOpen(false); }}
                      className={`w-full px-4 py-2 text-xs text-left flex items-center justify-between hover:bg-gray-50 ${market === m.code ? 'text-[#C6A15A] font-medium' : 'text-[#0B1E3F]'}`}
                      data-testid={`market-${m.code}`}
                    >
                      <span>{m.name[language] || m.name.en}</span>
                      <span className="text-gray-400">{m.currencyLabel}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Language */}
            <div
              className="relative hidden 2xl:block"
              onMouseEnter={() => setLangDropdownOpen(true)}
              onMouseLeave={() => setLangDropdownOpen(false)}
            >
              <button className="flex items-center gap-1 px-2 py-2 text-xs font-medium text-[#0B1E3F] hover:text-[#C6A15A] transition-colors" data-testid="language-toggle">
                <Globe className="h-4 w-4" />
                <span>{currentLang?.code.toUpperCase()}</span>
                <ChevronDown className="h-3 w-3" />
              </button>
              {langDropdownOpen && (
                <div className="absolute top-full right-0 w-36 bg-white shadow-lg rounded-sm py-2 border border-gray-100 z-50">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => { setLanguage(lang.code); setLangDropdownOpen(false); }}
                      className={`w-full px-4 py-2 text-xs text-left hover:bg-gray-50 ${language === lang.code ? 'text-[#C6A15A] font-medium' : 'text-[#0B1E3F]'}`}
                      data-testid={`lang-${lang.code}`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Auth */}
            {isAuthenticated ? (
              <div
                className="relative hidden 2xl:block"
                onMouseEnter={() => setUserDropdownOpen(true)}
                onMouseLeave={() => setUserDropdownOpen(false)}
              >
                <button className="flex items-center gap-2 px-2 py-2" data-testid="user-dropdown-btn">
                  <div className="w-8 h-8 bg-[#0B1E3F] text-[#C6A15A] rounded-full flex items-center justify-center text-xs font-bold">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                </button>
                {userDropdownOpen && (
                  <div className="absolute top-full right-0 w-48 bg-white shadow-lg rounded-sm py-2 border border-gray-100 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-[#0B1E3F]">{user?.name}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                    <button
                      onClick={() => { logout(); setUserDropdownOpen(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      data-testid="user-logout-btn"
                    >
                      <LogOut className="h-4 w-4" /> {ul.logout}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/contact"
                className="hidden 2xl:inline-flex items-center gap-2 px-4 py-2.5 bg-[#0B1E3F] text-white text-sm font-medium rounded-sm hover:bg-[#C6A15A] hover:text-[#0B1E3F] transition-all whitespace-nowrap"
                data-testid="header-cta"
              >
                {language === 'fr' ? 'Consultation' : language === 'es' ? 'Consulta' : language === 'ar' ? 'استشارة' : language === 'zh' ? '咨询' : 'Consultation'}
              </Link>
            )}

            {/* Mobile menu button — shown below xl (up to 1279px), hidden at xl+ */}
            <button
              className="2xl:hidden p-2 text-[#0B1E3F]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} />
        </div>

        {/* Mobile menu — below xl breakpoint */}
        {mobileMenuOpen && (
          <div className="2xl:hidden pb-4 border-t border-gray-100 max-h-[calc(100vh-96px)] overflow-y-auto" data-testid="mobile-menu">
            <div className="flex items-center justify-between px-3 py-3 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gray-500" />
                <select
                  value={market}
                  onChange={(e) => setMarket(e.target.value)}
                  className="bg-transparent text-sm font-medium text-[#0B1E3F] focus:outline-none"
                  data-testid="mobile-market-select"
                >
                  {MARKETS.map((m) => (
                    <option key={m.code} value={m.code}>{m.name[language] || m.name.en} ({m.currencyLabel})</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-gray-500" />
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="bg-transparent text-sm font-medium text-[#0B1E3F] focus:outline-none"
                  data-testid="mobile-language-select"
                >
                  {LANGUAGES.map((lang) => <option key={lang.code} value={lang.code}>{lang.name}</option>)}
                </select>
              </div>
            </div>

            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-3 py-3 text-sm font-medium ${isActive(link.path) ? 'text-[#C6A15A]' : 'text-[#0B1E3F]'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}

            <div className="px-3 pt-4 border-t border-gray-100 mt-2">
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full text-center px-4 py-3 bg-[#0B1E3F] text-white text-sm font-medium rounded-sm"
                data-testid="mobile-header-cta"
              >
                {language === 'fr' ? 'Demander une consultation' : 'Request consultation'}
              </Link>
            </div>

            {isAuthenticated && (
              <div className="px-3 pt-4 border-t border-gray-100 mt-2">
                <button
                  onClick={() => { logout(); setMobileMenuOpen(false); }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-red-600 border border-red-200 rounded-sm"
                  data-testid="mobile-logout-btn"
                >
                  <LogOut className="h-4 w-4" /> {ul.logout}
                </button>
              </div>
            )}
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
